import React, { FC } from 'react'
import { Tabs, Tab, TabList } from '@moonpig/launchpad-components'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { getDepartmentTitle } from '@moonpig/web-core-utils'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import {
  sanitizeContentType,
  useSearchAnalytics,
} from '../../pages/SearchPage/analytics'
import { departmentEvent } from '../../pages/SearchPage/events'
import { useFindLocaleText } from '../../text-localisation'
import { capitaliseFirstCharacter } from '../../utils'
import { SearchContext } from '../../store/slices/shared'
import { useSearchStore } from '../../store/SearchStore'

type DepartmentTabsProps = {
  currentDepartment: DepartmentsEnum
  searchContext: SearchContext
  withCounts?: boolean
}

const StyledTabs = styled(Tabs)`
  margin-bottom: -1px;

  [role='tab'] {
    flex: none;
  }

  [role='tablist'] {
    position: relative;
    border-bottom: none;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 100vw;
      transform: translateX(-50%);
      border-bottom: 1px solid;
      ${s({ borderColor: 'colorBorder03' })}
    }
  }
`

const StyledTab = styled(Tab)`
  ${s({
    px: { xs: 5, md: 7 },
    mb: 0,
  })}

  &:disabled {
    cursor: not-allowed;
    pointer-events: none;

    ${s({
      bgcolor: 'colorBlack00',
      color: 'colorInteractionDeactivated',
    })}
  }
`

const departmentOrder: { [key: string]: number } = {
  ALL_CARDS: 1,
  ALL_GIFTS: 2,
  ALL_FLOWERS_AND_PLANTS: 3,
}

export const DepartmentTabs: FC<DepartmentTabsProps> = ({
  currentDepartment,
  searchContext,
  withCounts = false,
}) => {
  const isLaunchpadAnalytics = useSearchAnalytics()

  const pageContext = useSearchStore(store => store.pageContext)

  const searchTerm =
    searchContext.searchTerm ||
    searchContext.selectedSuggestion?.label ||
    pageContext.title

  const t = useFindLocaleText()

  const onDepartmentChange = useSearchStore(store => store.changeDepartment)
  const departmentCounts = searchContext.departmentCounts

  const departments = departmentCounts
    .map(d => {
      const departmentName = `${capitaliseFirstCharacter(
        getDepartmentTitle(d.department),
      )}`
      return {
        ...d,
        displayName: t('find.department', {
          departmentName,
        }),
        order: departmentOrder[d.department],
      }
    })
    .sort((a, b) => a.order - b.order)

  return (
    <StyledTabs value={currentDepartment}>
      <TabList>
        {departments.map(({ department, displayName, count }) => {
          const ariaControlsId = `${displayName}-tab-panel`.toLowerCase()

          const departmentTitle = getDepartmentTitle(department)

          // Legacy event: apps/explore/find/src/store/middleware/trackEvent.ts
          const departmentClickEvent = departmentEvent()
            .withEventData({
              label:
                `${searchTerm} in ${departmentTitle} | ${count}`.toLowerCase(),
            })
            .withContentData({
              content_type: sanitizeContentType(
                `search | global header click | ${searchTerm} in ${departmentTitle} | ${count}`,
              ),
            })

          return (
            <StyledTab
              key={department}
              id={ariaControlsId}
              value={department}
              aria-controls={ariaControlsId}
              disabled={count === 0}
              events={
                isLaunchpadAnalytics
                  ? { onClick: departmentClickEvent }
                  : undefined
              }
              onClick={() => onDepartmentChange(department)}
              badgeCount={withCounts ? count : undefined}
            >
              {`${displayName}`}
            </StyledTab>
          )
        })}
      </TabList>
    </StyledTabs>
  )
}
